
import React, { useEffect } from 'react';
import { Pressable, Image, Platform } from 'react-native';
import { View, Text, Title, CheckBox } from '@constants/Themed';
import styles from '@stylesheet';
import { GoogleIcon, SmartSaaSIcon } from '@assets/--native';
import { Label, XStack, YStack } from 'tamagui';
import UniversalRating from '@components/UniversalRating';
import { LinearGradient } from 'expo-linear-gradient';

interface CompanyItemTypes {
  mapCallback?: (item: any) => void;
  selectCallback?: (item: any) => void;
  item: any;
  index: number;
  isSelected?: boolean;
  selectAll?: boolean;
}

const CompanyItem = ({ selectAll, mapCallback, selectCallback, isSelected, item, index }: CompanyItemTypes) => {
  const [selected, setSelected] = React.useState(isSelected || false);

  useEffect(() => {
    if (isSelected) {
      setSelected(true);
    }
  }, [selectAll]);

  const _handleSelect = () => {
    setSelected(!selected);
    selectCallback?.(item);
  }

  // Hero-inspired gradient colors
  const gradientColors = ['#C8A2C8', '#87CEEB']; // Purple to blue gradient matching Hero.tsx

  return (
    <View style={[styles.centerEverything, { flex: 1, alignSelf: "stretch", minWidth: "100%", flexDirection: "row" }]}>
      <View style={{ flex: 1, minWidth: 30 }}>
        <CheckBox value={selected} onValueChange={_handleSelect} />
      </View>
      <Pressable
        style={[
          styles.centerEverything,
          {
            minWidth: "80%",
            minHeight: 150,
            borderRadius: 16, // rounded-2xl equivalent
            alignSelf: "stretch",
            margin: 25,
            marginLeft: 10,
            gap: 20,
            padding: 16,
            flexDirection: "row",
            flex: 10,
            overflow: 'hidden',
            // Glass morphism shadow effects
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.3,
            shadowRadius: 8,
            elevation: 8,
            // Web backdrop blur for glass effect
            ...(Platform.OS === 'web' && {
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
            } as any),
          }
        ]}
        onPress={() => {
          mapCallback?.(item);
        }}
      >
        {/* Gradient Background Layer */}
        <LinearGradient
          colors={gradientColors}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            borderRadius: 16,
            opacity: 0.9,
          }}
        />

        {/* Content Container */}
        <View style={{ flexDirection: "row", flex: 1, zIndex: 1, gap: 16 }}>
          {
            item?.logo ?
              <View style={{ alignSelf: "stretch", flex: 1, borderRadius: 50, overflow: 'hidden', backgroundColor: 'rgba(255, 255, 255, 0.2)' }}>
                <Image source={{ uri: item?.logo }} style={{ width: 70, resizeMode: "contain", flex: 1 }} />
              </View>
              :
              <View style={[
                styles.centerEverything,
                {
                  backgroundColor: item.iconBackgroundColor || 'rgba(255, 255, 255, 0.2)',
                  justifyContent: "center",
                  alignSelf: "stretch",
                  flex: 1,
                  borderRadius: 50,
                  borderTopStartRadius: 20,
                  borderBottomStartRadius: 20,
                  overflow: 'hidden',
                  ...(Platform.OS === 'web' && {
                    backdropFilter: 'blur(10px)',
                  } as any),
                }
              ]}>
                {
                  item.type === 'google' ?
                    <GoogleIcon color="white" style={{ flex: 1, width: 40 }} />
                    :
                    <SmartSaaSIcon width={40} style={{ paddingRight: 10, flex: 1, width: 40, maxWidth: 40 }} />
                }
              </View>
          }
          <View style={{ flex: 3, justifyContent: 'center' }}>
            <Title underline style={{ color: 'white', marginBottom: 4 }}>
              {item.type === 'google' ? item.displayName?.text : item?.company_name}
            </Title>
            <Text style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: 13, marginBottom: 4 }}>
              {item.type === 'google' ? item.formattedAddress : item?.company_address}
            </Text>
            {item.websiteUri && (
              <Text style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: 12, marginBottom: 8 }}>
                {item.websiteUri}
              </Text>
            )}
            <YStack gap={5} marginTop={8}>
              <Label fontSize={12} color="rgba(255, 255, 255, 0.9)">Rating:</Label>
              <UniversalRating value={item.rating || 0} maxRating={5} size={18} readOnly />
            </YStack>
          </View>
        </View>
      </Pressable>
    </View>
  )
}
export default CompanyItem;